import {
  isNilOrZero,
} from '@populateui/utils';
import keyBy from 'lodash/keyBy';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  formatDateTimeOffset,
} from 'utils/date';
import {
  isNoAccessError,
  NoAccessOptions,
} from 'utils/errors';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  deleteAppointment,
  deleteAvailability,
  deleteBlockedTimeSlot,
  getAppointmentById,
  getAppointmentsDailyBreakdown,
  getBlockedTimeSlotById,
  getCalendarEventsByDateRange,
  getProviderAvailabilitySlotById,
  modifyAppointment,
  modifyAvailability,
  modifyBlockedTimeSlot,
  sendAppointmentReminder,
} from 'pages/Dashboard/pages/Appointments/pages/List/services/api';
import {
  CalendarEvent,
  CalendarEventWithPartialOptions,
  CalendarTimelineEvent,
  EventType,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/appointment';
import {
  AppointmentDTO,
  AppointmentReminderCallResponseDTO,
  BlockedTimeSlotDTO,
  InitiateCallResponseDTO,
  CreateAppointmentResponseDTO,
  DeleteAppointmentRequestDTO,
  DeleteCalendarSlotResponseDTO,
  EncounterNotificationDTO,
  ModifyAppointmentStatusResponseDTO,
  PatientDTO,
  PostApiCCMAgentInitiateCallParams,
  ProblemDetails,
  ProviderAvailabilityDTO,
  ReminderCommuniqueRequestDTO,
  PatientPaymentTransactionViewModelDTO,
  PostApiAppointmentReminderAgentCallForAppointmentReminderParams,
  PostApiTempoSendDayOneCommuniqueParams,
  None,
  PatientTempoStatus,
  CreatePatientRequestDTO,
  TelehealthProviderJoinInfoDTO,
  TelehealthMeetingStatusDTO,
} from 'dtos';
import {
  CalendarViewType,
  DATE_UNIT_BY_VIEW_TYPE,
  isCancelledOrNoShow,
} from 'pages/Dashboard/pages/Appointments/pages/List/utils/helper';
import {
  getColorByName,
} from 'utils/colors';
import {
  useColorsHashMap,
} from 'pages/Dashboard/pages/Encounters/hooks';
import {
  getApiBillingGetPatientPaymentTransactionsByEncounterId,
  getApiEncounterGetCalendarEventsByDateAndProvider,
  getApiTelehealthGetProviderJoinInfoEncounterId,
  getApiTempoGetTempoStatusByPatientId,
  postApiAppointmentReminderAgentCallForAppointmentReminder,
  postApiBlockedTimeSlotAddOrUpdate,
  postApiCCMAgentInitiateCall,
  postApiEncounterCreateAppointment,
  postApiEncounterModifyAppointmentPaymentType,
  postApiEncounterModifyAppointmentStatus,
  postApiPatientCreateNewPatient,
  postApiProviderAvailabilityAddOrUpdate,
  postApiTelehealthRetryMeetingCreationForAppointmentEncounterId,
  postApiTempoSendDayOneCommunique,
  postApiTempoSendDayTwoCommunique,
} from 'endpoints';
import {
  ModifyAppointmentStatusRequestDTO,
} from 'dtos/modifyAppointmentStatusRequestDTO';
import {
  ModifyAppointmentPaymentTypeRequestDTO,
} from 'dtos/modifyAppointmentPaymentTypeRequestDTO';
import {
  normalizeEvents,
} from 'pages/Dashboard/utils/helper';
import type {
  AppointmentForm,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details';
import {
  useNotify,
} from 'core/hooks';
import {
  useServiceFacilities,
} from 'views/EMR/stores/serviceFacility';
import {
  useContext,
  useMemo,
} from 'react';
import {
  FilterContext,
} from 'core/Filters/context';
import useSignalREffect from 'views/EMR/hooks/signalR';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import {
  appointmentsEventChannel,
} from 'pages/Dashboard/services/eventChannels';

export type CreateAppointmentRequestResponseDataType = BlockedTimeSlotDTO &
CreateAppointmentResponseDTO & ProviderAvailabilityDTO;
export type ModifyAppointmentRequestResponseDataType = BlockedTimeSlotDTO &
 AppointmentDTO & ProviderAvailabilityDTO;
type GeneralCalendarEvent = CalendarEvent | CalendarTimelineEvent;

const cssClassMap: Record<string, string> = {
  'Time Blocked': 'is-time-block-slot',
  Availability: 'is-provider-availability-slot',
};

export function useAppointments(
  date: Dayjs,
  viewType: 'Daily' | 'Weekly' | 'Monthly',
): UseQueryResult<GeneralCalendarEvent[], Error> {
  const colorsHashMap = useColorsHashMap();
  const serviceFacilities = useServiceFacilities();
  const serviceFacilityMap = keyBy(serviceFacilities ?? [], 'serviceFacility');

  const [start, end] = [
    formatDateTimeOffset(date.startOf(DATE_UNIT_BY_VIEW_TYPE[viewType])),
    formatDateTimeOffset(date.endOf(DATE_UNIT_BY_VIEW_TYPE[viewType])),
  ];

  const isMonthlyView = viewType === 'Monthly';

  return useQuery<GeneralCalendarEvent[], Error>({
    queryKey: ['appointments', viewType, start, end],
    queryFn: () => (
      isMonthlyView
        ? getAppointmentsDailyBreakdown(start, end)
        : getCalendarEventsByDateRange(start, end)
    ),
    select: (data: GeneralCalendarEvent[]) => (
      data.map((event) => {
        const serviceFacility = isMonthlyView
          ? serviceFacilityMap[event?.serviceFacilityId ?? '']
          : event.serviceFacility;
        const colorKey = event.isTimeAvailability
          ? serviceFacility?.nickname
          : event.typeOfVisitDescription;

        return ({
          ...event,
          cssClass: cssClassMap[event.typeOfVisitDescription ?? ''],
          color: getColorByName((colorsHashMap[colorKey ?? ''] ?? '!bg-emerald-200').replace('!bg-', '')),
          serviceFacility,
          typeOfVisitDescription: isMonthlyView && event.isTimeAvailability
            ? serviceFacility?.nickname ?? ''
            : event?.typeOfVisitDescription ?? '',
        });
      })
    ),
  });
}

const getEventDetailsFunctionList = Object.freeze({
  appointment: getAppointmentById,
  blockedTimeSlot: getBlockedTimeSlotById,
  providerAvailability: getProviderAvailabilitySlotById,
});

export function useGetAppointmentById(
  id: number,
  isTimeBlock: boolean,
  isAvailability: boolean,
  enabled: boolean = true,
  { throwOnNoAccess = false }: NoAccessOptions = {},
): UseQueryResult<CalendarEvent, Error> {
  const nonBlockTimeKey = isAvailability ? 'providerAvailability' : 'appointment';
  const fnKey = isTimeBlock ? 'blockedTimeSlot' : nonBlockTimeKey;

  return useQuery<any, Error>({
    queryKey: [fnKey, id],
    queryFn: ({ signal }) => getEventDetailsFunctionList[fnKey](id, signal),
    enabled,
    select: (data) => data ?? {},
    throwOnError: throwOnNoAccess && isNoAccessError,
  });
}

export function useGetCalendarEvents(
  date: string,
  providerId: number,
  enabled: boolean = false,
): UseQueryResult<GeneralCalendarEvent[], Error> {
  const colorsHashMap = useColorsHashMap();
  return useQuery<GeneralCalendarEvent[], Error>({
    queryKey: ['calendar-events', date, providerId],
    queryFn: async () => {
      const data = await getApiEncounterGetCalendarEventsByDateAndProvider(
        { date, providerId },
      );
      return normalizeEvents(data)
        .filter((event) => !isCancelledOrNoShow(event))
        .map((event) => {
          const { serviceFacility } = event;
          const colorKey = event.isTimeAvailability
            ? serviceFacility?.nickname
            : event.typeOfVisitDescription;
          return ({
            ...event,
            cssClass: cssClassMap[event.typeOfVisitDescription ?? ''],
            color: getColorByName((colorsHashMap[colorKey ?? ''] ?? '!bg-emerald-200').replace('!bg-', '')),
            serviceFacility,
            typeOfVisitDescription: event.isTimeAvailability
              ? serviceFacility?.nickname ?? ''
              : event?.typeOfVisitDescription ?? '',
          });
        });
    },
    enabled,
  });
}

type DeleteMutationResponse = DeleteAppointmentRequestDTO
  & ProblemDetails
  & DeleteCalendarSlotResponseDTO;
type AvailabilityUpdateMutationData = Partial<CalendarEvent> & AppointmentForm;

type CreateTransportFunction = (
  data: CreateAppointmentRequestResponseDataType,
) => Promise<CreateAppointmentRequestResponseDataType>;

type UpdateTransportFunction = (
  data: Partial<CalendarEvent>,
) => Promise<ModifyAppointmentRequestResponseDataType>;

type DeleteTransportFunction = (
  data: CalendarEventWithPartialOptions,
) => Promise<DeleteMutationResponse>;

type TransportFunctions = Record<EventType, {
  create: CreateTransportFunction;
  update: UpdateTransportFunction;
  remove: DeleteTransportFunction;
}>;

const transportFunctionsByType: TransportFunctions = Object.freeze({
  appointment: {
    create: (data) => postApiEncounterCreateAppointment(data),
    update: (data) => modifyAppointment(data),
    remove: (data) => (deleteAppointment(data) as Promise<DeleteMutationResponse>),
  },
  timeBlock: {
    create: (data) => postApiBlockedTimeSlotAddOrUpdate(data),
    update: (data) => modifyBlockedTimeSlot(data),
    remove: (data) => (deleteBlockedTimeSlot(data) as Promise<DeleteMutationResponse>),
  },
  availability: {
    create: (data) => postApiProviderAvailabilityAddOrUpdate(data),
    update: (data) => modifyAvailability(data as AvailabilityUpdateMutationData),
    remove: (data) => (deleteAvailability(data) as Promise<DeleteMutationResponse>),
  },
});

export function useDeleteAppointment():
  UseMutationResult<
    DeleteMutationResponse,
    Error,
    CalendarEventWithPartialOptions> {
  return useMutation({
    mutationFn: ({ data, options }: CalendarEventWithPartialOptions) => {
      const { remove } = transportFunctionsByType[data?.typeName ?? 'appointment'];
      return remove({ data, options });
    },
  });
}

export function useSendAppointmentReminder():
  UseMutationResult<void, Error, ReminderCommuniqueRequestDTO> {
  return useMutation<void, Error, ReminderCommuniqueRequestDTO>({
    mutationFn: sendAppointmentReminder,
  });
}

export function useModifyAppointment():
  UseMutationResult<ModifyAppointmentRequestResponseDataType, Error, Partial<CalendarEvent>> {
  return useMutation({
    mutationFn: (data) => {
      const { update } = transportFunctionsByType[data?.typeName ?? 'appointment'];
      return update(data);
    },
  });
}

export function useModifyAppointmentPaymentType():
  UseMutationResult<AppointmentDTO, Error, ModifyAppointmentPaymentTypeRequestDTO> {
  return useMutation({
    mutationFn: (
      data: ModifyAppointmentPaymentTypeRequestDTO,
    ) => postApiEncounterModifyAppointmentPaymentType(data),
  });
}

export function useSendModifyAppointmentStatusRequest():
  UseMutationResult<ModifyAppointmentStatusResponseDTO, Error, ModifyAppointmentStatusRequestDTO> {
  return useMutation<ModifyAppointmentStatusResponseDTO, Error, ModifyAppointmentStatusRequestDTO>({
    mutationFn: (data) => postApiEncounterModifyAppointmentStatus(data),
  });
}

export function useUpdateAppointmentStatus() {
  const { mutateAsync: modifyAppointmentStatus } = useSendModifyAppointmentStatusRequest();
  const notify = useNotify();

  const updateAppointmentStatus = async (data: ModifyAppointmentStatusRequestDTO) => {
    try {
      const response = await modifyAppointmentStatus(data);

      notify({
        message: `Appointment status has been updated.${response.isPaymentDeallocated ? ' Collected payments have been moved to the patient’s credit balance.' : ''}`,
        variant: 'success',
      });
      appointmentsEventChannel.emit('onEventUpdated', data.encounterId);
    } catch (error) {
      console.error(error);
    }
  };

  return { updateAppointmentStatus };
}

export function useCreateAppointment(eventType: EventType):
UseMutationResult<CreateAppointmentRequestResponseDataType, Error,
 CreateAppointmentRequestResponseDataType> {
  const { create } = transportFunctionsByType[eventType ?? 'appointment'];
  return useMutation<CreateAppointmentRequestResponseDataType,
    Error, CreateAppointmentRequestResponseDataType>({
      mutationFn: (data) => create(data),
    });
}

export function useSavePatient():
  UseMutationResult<PatientDTO, Error, Partial<CreatePatientRequestDTO>> {
  return useMutation<
    PatientDTO, Error, Partial<CreatePatientRequestDTO>>({
      mutationFn: (data) => postApiPatientCreateNewPatient(data as CreatePatientRequestDTO),
    });
}

export function useCallForAppointmentReminder(): UseMutationResult<
  AppointmentReminderCallResponseDTO,
  Error,
  PostApiAppointmentReminderAgentCallForAppointmentReminderParams> {
  return useMutation<AppointmentReminderCallResponseDTO,
    Error, PostApiAppointmentReminderAgentCallForAppointmentReminderParams>({
      mutationFn: (params) => postApiAppointmentReminderAgentCallForAppointmentReminder(params),
    });
}

export function useGetPatientPaymentTransactionsByEncounterId(encounterId: number) {
  return useQuery<PatientPaymentTransactionViewModelDTO[], Error>({
    queryKey: ['patient-payment-encounter', encounterId],
    queryFn: () => getApiBillingGetPatientPaymentTransactionsByEncounterId({ encounterId }),
    enabled: encounterId > 0,
  });
}

export function useCCMForCallPatient(): UseMutationResult<
InitiateCallResponseDTO, Error, PostApiCCMAgentInitiateCallParams> {
  return useMutation<InitiateCallResponseDTO, Error, PostApiCCMAgentInitiateCallParams>({
    mutationFn: (params) => postApiCCMAgentInitiateCall(params),
  });
}

export function useSendTempoLink(dayNumber: number): UseMutationResult<
  None, Error, PostApiTempoSendDayOneCommuniqueParams> {
  const mutationFn = dayNumber === 0
    ? postApiTempoSendDayOneCommunique
    : postApiTempoSendDayTwoCommunique;
  return useMutation<None, Error, PostApiTempoSendDayOneCommuniqueParams>({
    mutationFn: (params) => mutationFn(params),
  });
}

export function useGetPatientTempoStatus(
  patientId: number,
): UseQueryResult<PatientTempoStatus, Error> {
  return useQuery<PatientTempoStatus, Error>({
    queryKey: ['patient-tempo-status', patientId],
    queryFn: () => getApiTempoGetTempoStatusByPatientId({ patientId }),
    enabled: patientId > 0,
  });
}

export function useGetTelehealthJoinInfo(
  encounterId?: number,
  enabled: boolean = true,
): UseQueryResult<TelehealthProviderJoinInfoDTO, Error> {
  return useQuery<TelehealthProviderJoinInfoDTO, Error>({
    queryKey: ['telehealth-join-info', encounterId],
    queryFn: () => getApiTelehealthGetProviderJoinInfoEncounterId(encounterId ?? 0),
    enabled: enabled && !isNilOrZero(encounterId),
    retry: false,
  });
}

export function useRetryMeetingCreationForAppointment(
  encounterId: number,
): UseMutationResult<TelehealthMeetingStatusDTO, Error, void> {
  const queryClient = useQueryClient();

  return useMutation<TelehealthMeetingStatusDTO, Error, void>({
    mutationFn: () => postApiTelehealthRetryMeetingCreationForAppointmentEncounterId(encounterId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['telehealth-join-info', encounterId] });
      await queryClient.invalidateQueries({ queryKey: ['encounters'] });
    },
  });
}

export function useFetchTelehealthJoinInfo(): UseMutationResult<
  TelehealthProviderJoinInfoDTO, Error, number> {
  return useMutation<TelehealthProviderJoinInfoDTO, Error, number>({
    mutationFn: (encounterId) => getApiTelehealthGetProviderJoinInfoEncounterId(encounterId),
  });
}

const appointmentBaseProperties = [
  'durationInMinutes',
  'typeOfVisitDescription',
  'patientAppointmentStatus',
  'startDateTime',
  'serviceFacilityId',
  'canBeRescheduledSooner',
  'userId',
];

type AppointmentNotificationEffectProps = {
  onUpdate: () => void;
  dependencies?: any[];
  date?: Dayjs;
  viewType: CalendarViewType;
};

export function useAppointmentNotificationEffect({
  onUpdate,
  dependencies = [],
  date,
  viewType,
}: AppointmentNotificationEffectProps) {
  const { data: appointments } = useContext(FilterContext);
  const [start, end] = [
    formatDateTimeOffset(date?.startOf(DATE_UNIT_BY_VIEW_TYPE[viewType]) ?? ''),
    formatDateTimeOffset(date?.endOf(DATE_UNIT_BY_VIEW_TYPE[viewType]) ?? ''),
  ];

  const appointmentsByEncounterId = useMemo(() => (
    keyBy(appointments, 'encounterId')
  ), [appointments]);

  useSignalREffect('OnEncounterUpdated', (data: EncounterNotificationDTO) => {
    const { encounterId } = data;
    const existingEncounter = appointmentsByEncounterId[encounterId ?? ''];
    const isEncounterInView = !isNil(existingEncounter);

    const isDataChanged = isEncounterInView
      && !isEqual(
        pick({
          ...existingEncounter,
          serviceFacilityId: existingEncounter?.serviceFacility?.serviceFacilityId,
        }, appointmentBaseProperties),
        pick(data, appointmentBaseProperties),
      );
    const isDateChangedToRange = !isEncounterInView && dayjs(data.startDateTime).isBetween(start, end, 'day', '[]');

    if (isDataChanged || isDateChangedToRange) {
      onUpdate?.();
    }
  }, [appointmentsByEncounterId, ...dependencies]);

  useSignalREffect('OnEncounterDeleted', (data: EncounterNotificationDTO) => {
    const { encounterId } = data;
    const existingEncounter = appointmentsByEncounterId[encounterId ?? ''];

    if (!isNil(existingEncounter)) {
      onUpdate?.();
    }
  }, [appointmentsByEncounterId]);

  useSignalREffect('OnEncounterAdded', (data: EncounterNotificationDTO) => {
    onUpdate?.();
  }, [appointmentsByEncounterId]);
}
